import { useState, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { Plus, Pencil, Trash2, Receipt, X, Search, ChevronLeft, ChevronRight } from 'lucide-react'
import { toast } from 'sonner'
import { format } from 'date-fns'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Input } from '@/components/ui/input'

import { expensesApi, categoryMasterApi, usersApi } from '@/lib/api'
import type { ExpenseListItem, CategoryMasterListItem, UserListItem } from '@/types'
import { usePermissions } from '@/contexts/PermissionContext'

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100]
const ALL_CATEGORIES = 'ALL_CATEGORIES'
const ALL_USERS = 'ALL_USERS'

export default function ExpensesPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { canAddEdit } = usePermissions()
  const [deleteId, setDeleteId] = useState<string | null>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(25)

  // Filter form state (what user is typing/selecting)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>(ALL_CATEGORIES)
  const [selectedUserId, setSelectedUserId] = useState<string>(ALL_USERS)

  // Applied filters state (what is actually used for filtering)
  const [appliedSearchTerm, setAppliedSearchTerm] = useState('')
  const [appliedCategoryId, setAppliedCategoryId] = useState<string>(ALL_CATEGORIES)
  const [appliedUserId, setAppliedUserId] = useState<string>(ALL_USERS)

  // Fetch expenses (no server-side filtering, we'll filter client-side)
  const { data: expenses = [], isLoading } = useQuery<ExpenseListItem[]>({
    queryKey: ['expenses'],
    queryFn: () => expensesApi.getAll({}),
  })

  // Fetch expense categories for filter
  const { data: categories = [] } = useQuery<CategoryMasterListItem[]>({
    queryKey: ['categories', 'EXPENSE'],
    queryFn: () => categoryMasterApi.getAll(true, 'EXPENSE'),
  })

  // Fetch users for filter
  const { data: users = [] } = useQuery<UserListItem[]>({
    queryKey: ['users'],
    queryFn: () => usersApi.getAll(),
  })

  // Filter expenses based on APPLIED filters (client-side filtering)
  const filteredExpenses = useMemo(() => {
    return expenses.filter(expense => {
      // Search term filter (category name or remarks)
      if (appliedSearchTerm) {
        const searchLower = appliedSearchTerm.toLowerCase()
        const matchesCategory = expense.category_name?.toLowerCase().includes(searchLower)
        const matchesRemarks = expense.remarks?.toLowerCase().includes(searchLower)
        if (!matchesCategory && !matchesRemarks) return false
      }

      // Category filter
      if (appliedCategoryId !== ALL_CATEGORIES && expense.category_id !== appliedCategoryId) {
        return false
      }

      // User filter
      if (appliedUserId !== ALL_USERS && expense.user_id !== appliedUserId) {
        return false
      }

      return true
    })
  }, [expenses, appliedSearchTerm, appliedCategoryId, appliedUserId])

  // Pagination calculations
  const totalRecords = filteredExpenses.length
  const totalPages = Math.ceil(totalRecords / pageSize)

  const paginatedExpenses = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize
    const endIndex = startIndex + pageSize
    return filteredExpenses.slice(startIndex, endIndex)
  }, [filteredExpenses, currentPage, pageSize])

  const startRecord = totalRecords === 0 ? 0 : (currentPage - 1) * pageSize + 1
  const endRecord = Math.min(currentPage * pageSize, totalRecords)

  const handlePageSizeChange = (newSize: string) => {
    setPageSize(Number(newSize))
    setCurrentPage(1)
  }

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1)
    }
  }

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1)
    }
  }

  // Apply filters when Search is clicked
  const applyFilters = () => {
    setAppliedSearchTerm(searchTerm)
    setAppliedCategoryId(selectedCategoryId)
    setAppliedUserId(selectedUserId)
    setCurrentPage(1)
  }

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => expensesApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['expenses'] })
      toast.success('Expense deleted successfully')
      setDeleteId(null)
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to delete expense'
      toast.error(errorMessage)
    },
  })

  const handleDelete = (id: string) => {
    setDeleteId(id)
  }

  const confirmDelete = () => {
    if (deleteId) {
      deleteMutation.mutate(deleteId)
    }
  }

  // Check if any filters are applied (based on applied filters)
  const hasActiveFilters = appliedSearchTerm || appliedCategoryId !== ALL_CATEGORIES || appliedUserId !== ALL_USERS

  const clearAllFilters = () => {
    setSearchTerm('')
    setSelectedCategoryId(ALL_CATEGORIES)
    setSelectedUserId(ALL_USERS)
    setAppliedSearchTerm('')
    setAppliedCategoryId(ALL_CATEGORIES)
    setAppliedUserId(ALL_USERS)
    setCurrentPage(1)
  }

  const formatCurrency = (amount: number, currency: string = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    }).format(amount)
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between -mt-2">
        <div>
          <h1 className="text-2xl font-bold">{t('expenses.title')}</h1>
          <p className="text-sm text-muted-foreground">{t('expenses.manageExpenses')}</p>
        </div>
        {canAddEdit('EXPENSES') && (
          <Button onClick={() => navigate('/expenses/new')}>
            <Plus className="h-4 w-4 mr-2" />
            {t('expenses.addExpense')}
          </Button>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center -mb-1">
        {/* Search */}
        <div className="flex-1 min-w-[200px] max-w-[300px]">
          <Input
            placeholder={t('expenses.searchExpense')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && applyFilters()}
          />
        </div>

        {/* Category */}
        <Select value={selectedCategoryId} onValueChange={setSelectedCategoryId}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder={t('expenses.allCategories')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_CATEGORIES}>{t('expenses.allCategories')}</SelectItem>
            {categories.map((cat) => (
              <SelectItem key={cat.category_id} value={cat.category_id}>
                {cat.category_name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* User */}
        <Select value={selectedUserId} onValueChange={setSelectedUserId}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder={t('expenses.allUsers')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_USERS}>{t('expenses.allUsers')}</SelectItem>
            {users.map((user) => (
              <SelectItem key={user.user_id} value={user.user_id}>
                {user.full_name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Search Button */}
        <Button onClick={applyFilters}>
          <Search className="mr-2 h-4 w-4" />
          {t('common.search')}
        </Button>

        {/* Clear Button */}
        {hasActiveFilters && (
          <Button variant="ghost" size="sm" onClick={clearAllFilters}>
            <X className="mr-1 h-4 w-4" />
            {t('common.reset')}
          </Button>
        )}
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-muted-foreground">{t('expenses.loadingExpenses')}</div>
        </div>
      ) : expenses.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <div className="text-muted-foreground mb-4">
              {t('expenses.createFirstExpense')}
            </div>
            {canAddEdit('EXPENSES') && (
              <Button onClick={() => navigate('/expenses/new')}>
                <Plus className="mr-2 h-4 w-4" />
                {t('expenses.addExpense')}
              </Button>
            )}
          </CardContent>
        </Card>
      ) : filteredExpenses.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <div className="text-muted-foreground mb-4">
              {t('expenses.noMatchingExpenses')}
            </div>
            <Button variant="outline" onClick={clearAllFilters}>
              <X className="mr-2 h-4 w-4" />
              {t('common.reset')} {t('common.filters')}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="flex flex-col h-[calc(100vh-240px)]">
          <Card className="flex-1 overflow-hidden flex flex-col">
            <div className="flex-1 overflow-auto">
              <Table>
                <TableHeader className="sticky top-0 bg-background z-10">
                  <TableRow>
                    <TableHead>{t('billing.date')}</TableHead>
                    <TableHead>{t('expenses.category')}</TableHead>
                    <TableHead>{t('billing.amount')}</TableHead>
                    <TableHead>{t('expenses.staffUser')}</TableHead>
                    <TableHead>{t('expenses.remarks')}</TableHead>
                    <TableHead>{t('expenses.receipt')}</TableHead>
                    <TableHead>{t('common.status')}</TableHead>
                    <TableHead className="text-right">{t('common.actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedExpenses.map((expense) => (
                    <TableRow key={expense.expense_id}>
                      <TableCell>
                        {format(new Date(expense.expense_date), 'MMM dd, yyyy')}
                      </TableCell>
                      <TableCell>{expense.category_name}</TableCell>
                      <TableCell className="font-medium">
                        {formatCurrency(expense.amount, expense.currency)}
                      </TableCell>
                      <TableCell>{expense.user_name || '-'}</TableCell>
                      <TableCell className="max-w-[200px] truncate">
                        {expense.remarks || '-'}
                      </TableCell>
                      <TableCell>
                        {expense.has_receipt ? (
                          <Badge variant="secondary">
                            <Receipt className="h-3 w-3 mr-1" />
                            {t('common.yes')}
                          </Badge>
                        ) : (
                          <span className="text-muted-foreground">{t('common.no')}</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant={expense.is_active ? 'default' : 'secondary'}>
                          {expense.is_active ? t('billing.active') : t('billing.inactive')}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        {canAddEdit('EXPENSES') && (
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => navigate(`/expenses/${expense.expense_id}/edit`)}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDelete(expense.expense_id)}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            {/* Pagination Controls */}
            <div className="flex items-center gap-3 px-4 py-2 border-t">
              <Select value={String(pageSize)} onValueChange={handlePageSizeChange}>
                <SelectTrigger className="w-[70px] h-7 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PAGE_SIZE_OPTIONS.map((size) => (
                    <SelectItem key={size} value={String(size)}>
                      {size}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <span className="text-xs text-muted-foreground">
                {startRecord}-{endRecord} {t('common.of')} {totalRecords}
              </span>
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  onClick={goToPreviousPage}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  onClick={goToNextPage}
                  disabled={currentPage >= totalPages}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('expenses.deleteExpense')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('expenses.deleteConfirmation')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete}>{t('common.delete')}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

